import { useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { axios } from '@/services/axios';
import { toast } from 'react-toastify';

const saveTableState = async (existingSettings, tableName, tableState) => {
    const lastUpdate = Date.now();
    const updatedTableState = {
        ...(existingSettings.tableState || {}),
        [tableName]: tableState,
    };
    const data = {
        settings: {
            ...existingSettings,
            tableState: updatedTableState,
            tableColumnOrder: [],
            lastUpdate,
        },
    };

    const jsonString = `"${JSON.stringify(data).replaceAll('"', '\\"')}"`;
    const response = await axios.post('/adminuser/uisettings', jsonString, {
        headers: {
            'Content-Type': 'application/json',
        },
    });

    return response.data;
};

export const useSaveTableState = ({
    existingSettings = {},
    tableName,
    debounceMs = 1000,
} = {}) => {
    const debounceTimerRef = useRef(null);

    if (!tableName) {
        console.warn(
            'useSaveTableState: tableName is required to save table state properly',
        );
    }

    const mutation = useMutation({
        mutationFn: (tableState) =>
            saveTableState(existingSettings, tableName, tableState),
    });

    const saveState = useCallback(
        (tableState, callbacks = {}) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }

            mutation.mutate(tableState, {
                onSuccess: (data) => {
                    callbacks.onSuccess?.(data);
                    toast.success('Saved table state!');
                },
                onError: (error) => {
                    console.error('Error saving table state:', error);
                    toast.error('Error saving table state');
                    callbacks.onError?.(error);
                },
            });
        },
        [mutation],
    );

    const debouncedSaveState = useCallback(
        (tableState, callbacks = {}) => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                mutation.mutate(tableState, {
                    onSuccess: (data) => {
                        callbacks.onSuccess?.(data);
                    },
                    onError: (error) => {
                        console.error('Error saving table state:', error);
                        callbacks.onError?.(error);
                    },
                });
                debounceTimerRef.current = null;
            }, debounceMs);
        },
        [mutation, debounceMs],
    );

    const getSavedState = useCallback(() => {
        if (!tableName || !existingSettings.tableState) {
            return null;
        }
        return existingSettings.tableState[tableName] || null;
    }, [tableName, existingSettings.tableState]);

    return {
        saveState,
        debouncedSaveState,
        getSavedState,
        isSaving: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
    };
};

export default useSaveTableState;
